import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import CoachHomeLayout from "app/components/Layouts/CoachHomeLayout";
import SingleContact from "./_components/SingleContact";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { UserModel } from "app/models";
import { fetchDelete, fetchGet } from "app/utils/request";
import SearchBar from "app/components/inputs/SearchBar";
import { useTranslation } from "react-i18next";
import useDebounce from "app/utils/hooks/useDebounce";
import { usePagination } from "app/utils/hooks/usePagination";
import { Loading } from "app/components/Wrappers";
import noContacts from "app/images/no_contacts.png";
import plus_circle from "app/images/plus_circle.svg";
import InfoModal from "../Calendar/InfoModal";
import AddNewContactManuallyModal from "./_components/AddNewContactManuallyModal";
// TO DO
// import AddNewContactModal from "./_components/AddNewContactModal";
import NoFilteredResults from "app/components/Layouts/NoFilteredResults";
import ButtonVariant from "app/components/Button/ButtonVariant";

const INIT_PAGE = 1;
const PER_PAGE = 20;

const DEFAULT_POPUP_CONTENT: IPopupContent = {
  status: "success",
  title: "",
  content: "",
};

interface IPopupContent {
  status: "success" | "error";
  title: string;
  content: string;
}

enum MODAL_TYPES {
  ADD_CONTACT_MODAL = "ADD_CONTACT_MODAL",
  ADD_CONTACT_MANUALLY_MODAL = "ADD_CONTACT_MANUALLY_MODAL",
  ADD_CONTACT_VIA_CSV_MODAL = "ADD_CONTACT_VIA_CSV_MODAL",
}

{
  /* TODO: uncomment modals and modal functions when we will have support for adding contacts manually */
}

const Contacts = () => {
  const { t } = useTranslation();
  const { data: facility } = useCurrentFacility();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [currentModal, setCurrentModal] = useState<MODAL_TYPES | null>(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [infoPopup, setInfoPopup] = useState<IPopupContent>(
    DEFAULT_POPUP_CONTENT,
  );
  const [loading, setLoading] = useState(false);

  const openModal = (modalType: MODAL_TYPES) => {
    setCurrentModal(modalType);
  };

  const closeModal = () => {
    setCurrentModal(null);
  };

  const switchModal = (modalType: MODAL_TYPES) => {
    setCurrentModal(currentModal === modalType ? null : modalType);
  };

  const addNewContactManuallyModalHandler = () => {
    if (currentModal === MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL) {
      closeModal();
    } else {
      switchModal(MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL);
    }
  };

  // TO DO
  // const addNewContactModalHandler = () => {
  //   closeModal();
  // };

  // const addNewContactViaCsvModalHandler = () => {
  //   if (currentModal === MODAL_TYPES.ADD_CONTACT_VIA_CSV_MODAL) {
  //     closeModal();
  //   } else {
  //     switchModal(MODAL_TYPES.ADD_CONTACT_VIA_CSV_MODAL);
  //   }
  // };

  // const onContactsUpload = (file: File) => {
  //   console.log("file: ", file);
  // };

  const handleDelete = async (userId: string): Promise<void> => {
    if (facility?.id) {
      setLoading(true);
      try {
        await fetchDelete(`/facilities/${facility.id}/contacts/${userId}`);

        setInfoPopup({
          title: t("Contact Deleted"),
          content: t("You successfully deleted the contact"),
          status: "success",
        });
        setIsInfoModalOpen((prev) => !prev);
        updateData((data) => data?.filter((a: UserModel) => a?.id !== userId));
        setLoading(false);
      } catch (error) {
        if (error.status === 422 && error.originalErrors?.contact) {
          setInfoPopup({
            title: t("Cannot Delete Contact"),
            content: t("Contacts with event history cannot be removed from the system."),
            status: "error",
          });
        } else {
          setInfoPopup({
            title: t("Something went wrong"),
            content: t("Please try again later"),
            status: "error",
          });
        }
        setIsInfoModalOpen(true);
        setLoading(false);
      }
    }
  };

  const {
    data: students = [],
    pending,
    sentryRef,
    loadInitialData,
    updateData,
  } = usePagination({
    fetchOnMount: false,
    initialParams: {
      page: INIT_PAGE,
      perPage: PER_PAGE,
      query: debouncedSearch || undefined,
    },
    fetchData: async (params) => {
      if (facility?.id) {
        const resp = await fetchGet(
          `/facilities/${facility?.id}/contacts`,
          // Browsing nests children under their parent; searching returns the
          // matching members flat. Either way a managed child carries its
          // guardians, so the "Managed by" line shows in both cases.
          params.query
            ? { ...params, options: { include: ["guardians"] } }
            : { ...params, options: { include: ["children", "guardians"] } },
        );
        return resp;
      } else {
        return [];
      }
    },
    clearDataAction: () => {},
    dependencies: [debouncedSearch],
  });

  const onUserUpdate = (user: UserModel) => {
    updateData((data: UserModel[]) =>
      data?.map((item) => (item.id === user.id ? user : item)),
    );
  };

  const hasLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    if (facility?.id && hasLoadedRef.current !== facility.id) {
      loadInitialData({
        page: 1,
        perPage: PER_PAGE,
        query: debouncedSearch || undefined,
      });
      hasLoadedRef.current = facility.id;
    }
  }, [facility?.id]);

  const reloadContacts = () => {
    loadInitialData({
      page: INIT_PAGE,
      perPage: PER_PAGE,
      query: debouncedSearch || undefined,
    });
  };

  if (students?.length === 0 && !pending && !debouncedSearch) {
    return (
      <CoachHomeLayout>
        <div className={styles.empty_container}>
          <img
            src={noContacts}
            alt="empty contacts"
            className={styles.empty_image}
          />
          <div className={styles.empty_text_wrapper}>
            <p className={styles.welcome_text}>{t("Welcome to Contacts")}</p>
            <p className={styles.added_contacts_text}>
              {t("All your added contacts will be displayed here")}
            </p>
          </div>
          <div className={styles.btn_wrapper}>
            <ButtonVariant
              buttonStyles={styles.add_btn}
              buttonType="primary"
              title={t("Add Contact")}
              icon={<i className="ico ico-add" />}
              onClickFunc={() =>
                openModal(MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL)
              }
            />
          </div>
        </div>
        <AddNewContactManuallyModal
          isOpen={currentModal === MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL}
          modalHandler={addNewContactManuallyModalHandler}
          setOpenInfoModal={setIsInfoModalOpen}
          setModalContent={setInfoPopup}
          facilityId={facility?.id}
          onContactAdded={reloadContacts}
        />
        <InfoModal
          isOpen={isInfoModalOpen}
          title={infoPopup.title}
          status={infoPopup.status}
          onDismiss={() => setIsInfoModalOpen(false)}
        >
          <div>{infoPopup.content}</div>
        </InfoModal>
      </CoachHomeLayout>
    );
  }

  return (
    <CoachHomeLayout>
      <div className={styles.header_wrapper}>
        <SearchBar
          className={styles.search_input}
          placeholder={t("Search contacts...")}
          onChange={setSearchTerm}
        />
        <button
          className={styles.add_button}
          // TODO: when we will have support for adding contacts manually this should be set to ADD_CONTACT_MODAL
          // onClick={() => openModal(MODAL_TYPES.ADD_CONTACT_MODAL)}
          onClick={() => openModal(MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL)}
        >
          <img src={plus_circle} alt="add contact" />
          <p>{t("Add Contacts")}</p>
        </button>
      </div>
      <div className={styles.contacts_list}>
        {students?.length === 0 && !pending && debouncedSearch?.length > 0 && <NoFilteredResults missingTerm={debouncedSearch} />}
        {students.map((student: UserModel) => (
          <React.Fragment key={student?.id}>
            <SingleContact
              user={student}
              handleDelete={(userId: string) => handleDelete(userId)}
              loading={loading || pending}
              facilityId={facility?.id}
              onUserUpdate={onUserUpdate}
            />
            {student?.children?.map((child: UserModel) => (
              <SingleContact
                key={child?.id}
                user={child}
                handleDelete={(userId: string) => handleDelete(userId)}
                loading={loading || pending}
                facilityId={facility?.id}
                onUserUpdate={onUserUpdate}
              />
            ))}
          </React.Fragment>
        ))}
        {pending && <Loading isLoading={pending} loadType="spinner" />}
        {students?.length > 0 && <div ref={sentryRef} />}
      </div>
      {/* <AddNewContactModal
        isOpen={currentModal === MODAL_TYPES.ADD_CONTACT_MODAL}
        onClose={addNewContactModalHandler}
        onOpenManuallyModal={() =>
          switchModal(MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL)
        }
        onOpenCsvModal={() =>
          switchModal(MODAL_TYPES.ADD_CONTACT_VIA_CSV_MODAL)
        }
      /> */}

      <AddNewContactManuallyModal
        isOpen={currentModal === MODAL_TYPES.ADD_CONTACT_MANUALLY_MODAL}
        modalHandler={addNewContactManuallyModalHandler}
        setOpenInfoModal={setIsInfoModalOpen}
        setModalContent={setInfoPopup}
        facilityId={facility?.id}
        onContactAdded={reloadContacts}
      />

      {/* <AddNewContactViaCsvModal */}
      {/*   isOpen={currentModal === MODAL_TYPES.ADD_CONTACT_VIA_CSV_MODAL} */}
      {/*   onClose={addNewContactViaCsvModalHandler} */}
      {/*   onUpload={onContactsUpload} */}
      {/* /> */}
      <InfoModal
        isOpen={isInfoModalOpen}
        title={infoPopup.title}
        status={infoPopup.status}
        onDismiss={() => setIsInfoModalOpen(false)}
      >
        <div>{infoPopup.content}</div>
      </InfoModal>
    </CoachHomeLayout>
  );
};

export default Contacts;
